import React, { useEffect, useRef, useState } from 'react';
import styles from './ChatWindow.module.css';
import { ChatBubble } from './ChatBubble';
import logger from '../../../logging/logger';
import { MessageFeedback } from '../../../services/SessionService';

interface Message {
  id: string;
  user: string;
  text: string;
  feedback?: MessageFeedback | null;
  content?: {
    text?: string;
    chart?: any;
    data?: any;
    strategy_config?: any;
    embedded_chart?: any;
    backtest_progress?: {
      progress: number;
      phase: 'planning' | 'loading_data' | 'executing' | 'loading_positions' | 'complete' | 'failed';
      currentPeriod?: string;
      isComplete?: boolean;
      error?: string;
    };
  };
  onDownloadExcel?: () => void;
}

interface ChatWindowProps {
  messages: Message[];
  isBottom: boolean;
  isLoading?: boolean;
  isWaitingForResponse?: boolean; // Show "Thinking" indicator
  isStreaming?: boolean; // Show streaming dot
  onLoadMore?: () => void;
  hasMore?: boolean;
  isLoadingMore?: boolean;
  sessionId?: string;
  agentActivity?: string | null; // Dynamic activity text (e.g., "Analyzing data...")
  showScrollButton?: boolean;
  onScrollToBottom?: () => void;
  onScrollButtonVisibilityChange?: (visible: boolean) => void;
  onMessageFeedbackChange?: (messageId: string, feedback: MessageFeedback | null) => Promise<boolean>;
}

export const ChatWindow: React.FC<ChatWindowProps> = ({
  messages,
  isBottom,
  isLoading,
  isWaitingForResponse = false,
  isStreaming = false,
  onLoadMore,
  hasMore = false,
  isLoadingMore = false,
  sessionId,
  agentActivity = null,
  showScrollButton = false,
  onScrollToBottom,
  onScrollButtonVisibilityChange,
  onMessageFeedbackChange
}) => {
  const windowRef = useRef<HTMLDivElement>(null);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const [canLoadMore, setCanLoadMore] = useState(false);
  const prevScrollHeight = useRef(0);
  const prevScrollTop = useRef(0);
  const isLoadingMoreRef = useRef(isLoadingMore);
  const prevMessageCount = useRef(messages.length);
  const isInitialLoad = useRef(true);
  const hasScrolledToBottom = useRef(false);

  // Reference to track the last user message for scrolling
  const lastUserMessageRef = useRef<HTMLDivElement>(null);

  // Reset initial load state when session changes
  useEffect(() => {
    isInitialLoad.current = true;
    hasScrolledToBottom.current = false;
    prevMessageCount.current = 0;
    logger.info('📍 Session changed, reset initial load state');
  }, [sessionId]);

  // Keep isLoadingMore ref in sync
  useEffect(() => {
    isLoadingMoreRef.current = isLoadingMore;
  }, [isLoadingMore]);

  // CONSOLIDATED SCROLL LOGIC: Handle all scroll scenarios in proper order
  useEffect(() => {
    if (!windowRef.current) return;

    const currentMessageCount = messages.length;
    const messagesAdded = currentMessageCount - prevMessageCount.current;

    // Scenario 1: Initial session load - scroll to bottom IMMEDIATELY
    if (isInitialLoad.current && !hasScrolledToBottom.current && messages.length > 0) {
      // Scroll immediately without requestAnimationFrame to prevent glitch
      windowRef.current.scrollTop = windowRef.current.scrollHeight;
      hasScrolledToBottom.current = true;
      isInitialLoad.current = false;
    }
    // Scenario 2: Pagination - preserve scroll position when loading older messages
    else if (messagesAdded > 0 && prevScrollHeight.current > 0 && isLoadingMoreRef.current) {
      requestAnimationFrame(() => {
        if (!windowRef.current) return;
        
        const newScrollHeight = windowRef.current.scrollHeight;
        const heightDiff = newScrollHeight - prevScrollHeight.current;
        
        if (heightDiff > 0) {
          windowRef.current.scrollTop = prevScrollTop.current + heightDiff;
          logger.info('📍 Scroll position preserved:', {
            prevHeight: prevScrollHeight.current,
            newHeight: newScrollHeight,
            heightDiff,
            prevScrollTop: prevScrollTop.current,
            newScrollTop: windowRef.current.scrollTop,
            messagesAdded
          });
        }
      });
    }
    // Scenario 3: New user message - scroll user message to top
    else if (lastUserMessageRef.current && !isLoadingMoreRef.current && !isInitialLoad.current && messagesAdded > 0 && messagesAdded <= 2) {
      // Check if the last message added was a user message
      const lastMessage = messages[messages.length - 1];
      const secondLastMessage = messages[messages.length - 2];
      
      const isNewUserMessage = (lastMessage?.user === 'user' || lastMessage?.user === 'You') ||
                              (secondLastMessage?.user === 'user' || secondLastMessage?.user === 'You');
      
      logger.info('🔍 User message check:', {
        lastMessage: lastMessage?.user,
        secondLastMessage: secondLastMessage?.user,
        isNewUserMessage,
        messagesAdded
      });
      
      if (isNewUserMessage) {
        requestAnimationFrame(() => {
          if (lastUserMessageRef.current) {
            lastUserMessageRef.current.scrollIntoView({ block: 'start' });
            logger.info('📍 Auto-scrolled to last user message');
          }
        });
      }
    }

    // Update refs for next time - ALWAYS DO THIS LAST
    if (windowRef.current) {
      prevScrollHeight.current = windowRef.current.scrollHeight;
      prevScrollTop.current = windowRef.current.scrollTop;
    }
    prevMessageCount.current = currentMessageCount;
  }, [messages]);

  // Separate effect for enabling load more (to avoid triggering scroll logic)
  useEffect(() => {
    if (messages.length > 0 && !canLoadMore) {
      setTimeout(() => setCanLoadMore(true), 500);
    }
  }, [messages.length, canLoadMore]);

  // Handle scroll events
  const handleScroll = () => {
    if (!windowRef.current || !canLoadMore) return;

    const { scrollTop, scrollHeight, clientHeight } = windowRef.current;
    const isAtTop = scrollTop <= 10;
    const isAtBottom = scrollTop + clientHeight >= scrollHeight - 50; // 50px threshold

    // Update scroll position tracking
    prevScrollTop.current = scrollTop;

    // Show scroll button when user is away from the last user message AND not at bottom
    let shouldShow = !isAtBottom;
    if (shouldShow && lastUserMessageRef.current) {
      const messageTop = lastUserMessageRef.current.offsetTop - 20; // Account for scroll-margin
      const isAtLastUserMessage = Math.abs(scrollTop - messageTop) < 50;
      if (isAtLastUserMessage) shouldShow = false;
    }
    setShowScrollToBottom(shouldShow);
    if (onScrollButtonVisibilityChange) {
      onScrollButtonVisibilityChange(shouldShow);
    }

    // Load more when scrolled to top
    if (isAtTop && !isLoadingMore && hasMore && onLoadMore) {
      logger.info('🔄 Loading more messages, current scroll position:', scrollTop);
      // Capture current state before loading
      prevScrollHeight.current = windowRef.current.scrollHeight;
      prevScrollTop.current = scrollTop;
      onLoadMore();
    }
  };

  // Scroll to last user message (for button click) so user+AI pair is visible
  const scrollToLastUserMessage = () => {
    if (lastUserMessageRef.current) {
      lastUserMessageRef.current.scrollIntoView({ block: 'start', behavior: 'smooth' });
    } else if (windowRef.current) {
      // Fallback to bottom if no user message ref found
      windowRef.current.scrollTo({
        top: windowRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
  };

  // Expose scroll function to parent via callback
  useEffect(() => {
    if (onScrollToBottom) {
      // Replace parent's scroll function with our internal one
      (window as any).__chatWindowScrollToBottom = scrollToLastUserMessage;
    }
  }, [onScrollToBottom]);

  return (
    <div
      ref={windowRef}
      className={styles.chatWindowContainer}
      onScroll={handleScroll}
    >
      <div
        className={`${styles.window} ${isBottom ? styles.bottom : ''}`}
      >
        {isLoadingMore && (
          <div className={styles.loadMoreIndicator}>
            <div className={styles.loadingSpinner}>Loading older messages...</div>
          </div>
        )}
        
        {(() => {
          // Find index of last user message
          let lastUserMsgIndex = -1;
          for (let i = messages.length - 1; i >= 0; i--) {
            if (messages[i].user === 'user' || messages[i].user === 'You') {
              lastUserMsgIndex = i;
              break;
            }
          }

          return messages.map((msg, index) => {
            const textPreview = typeof msg.text === 'string' && msg.text.length > 0
              ? msg.text.slice(0, 20)
              : `msg-${index}`;
            const isLastUserMessage = index === lastUserMsgIndex;

            return (
              <div
                key={msg.id || `${msg.user}-${index}-${textPreview}`}
                className={styles.animatedBubble}
                data-user={msg.user}
                ref={isLastUserMessage ? lastUserMessageRef : null}
              >
                <ChatBubble
                  messageId={msg.id}
                  user={msg.user}
                  text={msg.text}
                  feedback={msg.feedback}
                  content={msg.content}
                  onDownloadExcel={msg.onDownloadExcel}
                  sessionId={sessionId}
                  onFeedbackChange={onMessageFeedbackChange}
                />
              </div>
            );
          });
        })()}

        {/* Thinking indicator - shows before AI starts responding */}
        {(isLoading || isWaitingForResponse) && !isStreaming && (
          <div className={styles.loadingIndicator}>
            <div className={styles.thinkingIndicator}>
              <div className={styles.thinkingOrb}></div>
              <span className={styles.thinkingText}>{agentActivity || 'Thinking'}</span>
            </div>
          </div>
        )}

        {/* Streaming indicator - small dot while AI is typing */}
        {isStreaming && (
          <div className={styles.streamingIndicator}>
            <div className={styles.streamingDot}></div>
          </div>
        )}

        {/* Spacer to ensure there's enough scrollable space to position last user message at top */}
        <div className={styles.bottomSpacer}></div>
      </div>
    </div>
  );
};
